const watchFoldersAutomationsContent = {
  title: "Watch Folders & Automations",
  description: "Assign automations to a folder and have them run on their own when new files arrive, with one-click undo on every run.",
  sections: [
    {
      id: "overview",
      title: "What Watch Folders and Automations Are",
      content: `
        <p class="text-[#1d1d1f] text-lg mb-6">
          <strong>Automations</strong> are condition-action rules: "when a file matches these conditions,
          do this." <strong>Watch folders</strong> are what happens when you attach automations to a
          <a href="/docs/spaces" class="text-[#06c]">Space</a> and tell Sortio to run them whenever files
          are added to that folder. Drop a file in, and Sortio files it where your rules say it belongs,
          in the background, with a one-click undo on every run.
        </p>

        <p class="text-[#1d1d1f] text-lg mb-6">
          As of Sortio v8.18.0 the pieces fit together like this:
        </p>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div class="border border-[#e0e0e5] rounded-xl p-5 bg-white/80 backdrop-blur">
            <h4 class="font-semibold text-[#1d1d1f] mb-2">1. Build automations</h4>
            <p class="text-[#424245] text-sm">Create rules with the manual builder (free) or by describing them in plain English (uses a small part of your AI allowance).</p>
          </div>
          <div class="border border-[#e0e0e5] rounded-xl p-5 bg-white/80 backdrop-blur">
            <h4 class="font-semibold text-[#1d1d1f] mb-2">2. Assign them to a Space</h4>
            <p class="text-[#424245] text-sm">Open a Space, switch to its Automations tab, and check the rules you want active in that folder.</p>
          </div>
          <div class="border border-[#06c]/20 rounded-xl p-5 bg-[#06c]/5 backdrop-blur">
            <h4 class="font-semibold text-[#1d1d1f] mb-2">3. Pick when they run</h4>
            <p class="text-[#424245] text-sm">Manually, when you open the Space, or automatically whenever files are added.</p>
          </div>
        </div>

        <p class="text-[#1d1d1f] text-lg">
          This page covers assigning automations and the auto-run modes. For the full reference on
          conditions, comparators, actions, and boolean logic, see the
          <a href="/docs/rules-engine" class="text-[#06c]">Rules Engine</a> documentation.
        </p>
      `
    },
    {
      id: "creating-automations",
      title: "Creating an Automation",
      content: `
        <p class="text-[#1d1d1f] text-lg mb-6">
          You can create automations two ways. Both produce the same kind of rule; the difference is how
          you author it.
        </p>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div class="border border-[#06c]/20 rounded-xl p-5 bg-[#06c]/5 backdrop-blur">
            <h4 class="font-semibold text-[#1d1d1f] mb-2">AI-generated</h4>
            <p class="text-[#424245] text-sm mb-3">Describe what you want in plain English ("Move all PDF files larger than 10MB to my Archives folder") and Sortio translates it into a structured rule you can inspect and edit.</p>
            <p class="text-[#86868b] text-xs">Each generation uses <strong>a small part of your AI allowance</strong>. Refining an existing rule with follow-up prompts is free.</p>
          </div>
          <div class="border border-[#e0e0e5] rounded-xl p-5 bg-white/80 backdrop-blur">
            <h4 class="font-semibold text-[#1d1d1f] mb-2">Manual rule builder</h4>
            <p class="text-[#424245] text-sm mb-3">Pick conditions (name, extension, size, dates, EXIF data, content, Finder tags) and actions (move, rename, tag) from dropdowns.</p>
            <p class="text-[#86868b] text-xs">Doesn't use your AI allowance at all. Full control over every field.</p>
          </div>
        </div>

        <div class="border border-[#e0e0e5] rounded-xl bg-white/80 backdrop-blur overflow-hidden mb-6">
          <div class="p-6 space-y-4">
            <div>
              <h4 class="font-semibold text-[#1d1d1f] mb-2">Template variables and fallbacks</h4>
              <p class="text-[#424245]">Move destinations and rename patterns accept variables like <code>{file.name}</code>, <code>{file.extension}</code>, and <code>{file.exif.datetime}</code>. Any variable can take a fallback with a pipe: <code>{file.exif.datetime|Other}</code> sends files that are missing the field (screenshots, videos, exports) to an <code>Other</code> folder instead of stopping the run. Add a fallback to any variable that some files might not have.</p>
            </div>
            <div>
              <h4 class="font-semibold text-[#1d1d1f] mb-2">Dry Run first</h4>
              <p class="text-[#424245]">Every automation can be executed as a <strong>Dry Run</strong>: Sortio shows exactly which files would match and what would happen to them, without touching anything on disk. Run it before enabling any new or edited rule.</p>
            </div>
          </div>
        </div>

        <p class="text-[#1d1d1f] text-lg">
          After creating an automation you can run it on a folder immediately, and the Sort screen keeps
          your saved automations one click away.
        </p>
      `
    },
    {
      id: "run-modes",
      title: "Assigning Automations and Choosing When They Run",
      content: `
        <p class="text-[#1d1d1f] text-lg mb-6">
          Automations do their ongoing work inside a Space. Open a Space, go to its
          <strong>Automations</strong> tab, click <strong>Edit</strong>, and check the automations you
          want active in that folder. Then choose one of three run modes for the Space:
        </p>

        <div class="border border-[#e0e0e5] rounded-xl bg-white/80 backdrop-blur overflow-hidden mb-6">
          <div class="p-6 space-y-6">
            <div>
              <h5 class="font-medium text-[#1d1d1f] flex items-center">
                <div class="rounded-full bg-[#06c] h-7 w-7 flex items-center justify-center mr-3 shrink-0">
                  <span class="text-white font-medium text-sm">1</span>
                </div>
                Manually
              </h5>
              <p class="mt-2 text-[#424245] ml-10">Assigned automations run only when you click <strong>Run Automations</strong>. Good for cleanup passes you want to supervise.</p>
            </div>
            <div>
              <h5 class="font-medium text-[#1d1d1f] flex items-center">
                <div class="rounded-full bg-[#06c] h-7 w-7 flex items-center justify-center mr-3 shrink-0">
                  <span class="text-white font-medium text-sm">2</span>
                </div>
                When I open this Space
              </h5>
              <p class="mt-2 text-[#424245] ml-10">Assigned automations run automatically each time you open the Space, moving any matching files that have accumulated since your last visit.</p>
            </div>
            <div>
              <h5 class="font-medium text-[#1d1d1f] flex items-center">
                <div class="rounded-full bg-[#06c] h-7 w-7 flex items-center justify-center mr-3 shrink-0">
                  <span class="text-white font-medium text-sm">3</span>
                </div>
                When files are added
              </h5>
              <p class="mt-2 text-[#424245] ml-10">The watch-folder mode. Sortio watches the folder in the background and runs your automations on new files as they land, even when the Space isn't open. Every run can be undone with one click.</p>
            </div>
          </div>
        </div>

        <p class="text-[#1d1d1f] text-lg">
          The Space card shows which mode is active (for example "Auto-run: new files") and how many
          automations are assigned, so you can see at a glance which folders are managing themselves.
        </p>
      `
    },
    {
      id: "on-change",
      title: "How Auto-Run on New Files Works",
      content: `
        <p class="text-[#1d1d1f] text-lg mb-6">
          With <strong>When files are added</strong> selected, Sortio's background watcher picks up new
          files in the Space's folder and runs the assigned automations against them. You don't need the
          Space open; you don't need to click anything.
        </p>

        <div class="border border-[#e0e0e5] rounded-xl bg-white/80 backdrop-blur overflow-hidden mb-6">
          <div class="p-6 space-y-4">
            <div>
              <h4 class="font-semibold text-[#1d1d1f] mb-2">Files are processed once they're complete</h4>
              <p class="text-[#424245]">Sortio waits briefly to make sure a file has finished writing (a download still in progress, for example) before processing it.</p>
            </div>
            <div>
              <h4 class="font-semibold text-[#1d1d1f] mb-2">Only new files are touched</h4>
              <p class="text-[#424245]">Files the automations already handled aren't re-processed on every change. Each run works on what's new.</p>
            </div>
            <div>
              <h4 class="font-semibold text-[#1d1d1f] mb-2">You're told what happened</h4>
              <p class="text-[#424245]">After a background run, Sortio shows a notification like "Downloads: moved 4 files automatically" with an <strong>Undo</strong> button right on it. One click puts everything back.</p>
            </div>
            <div>
              <h4 class="font-semibold text-[#1d1d1f] mb-2">Runs are free</h4>
              <p class="text-[#424245]">Automation runs are deterministic, free, and unlimited, no matter how often they fire. (Generating a rule with AI uses a small part of your AI allowance; running rules never touches it.)</p>
            </div>
          </div>
        </div>

        <div class="border border-amber-100 rounded-xl p-5 bg-amber-50/80 backdrop-blur">
          <h4 class="font-semibold text-[#1d1d1f] flex items-center mb-2">
            <span class="mr-2">ℹ️</span>
            No scheduled runs
          </h4>
          <p class="text-[#424245] text-sm">Sortio does not currently run automations on a timer or recurring schedule. Automations run manually, when you open a Space, or when files are added to a watched folder. If you need periodic cleanup of a folder that doesn't receive new files, run the automation manually.</p>
        </div>
      `
    },
    {
      id: "safety",
      title: "Safety: Protected Folders, Dry Run, and Undo",
      content: `
        <p class="text-[#1d1d1f] text-lg mb-6">
          Background file-moving needs guardrails, and Sortio ships several. The full picture is on the
          <a href="/docs/file-safety" class="text-[#06c]">Undo, Preview &amp; File Safety</a> page; here's
          what applies to automations specifically.
        </p>

        <div class="border border-[#e0e0e5] rounded-xl bg-white/80 backdrop-blur overflow-hidden mb-6">
          <div class="p-6 space-y-4">
            <div>
              <h4 class="font-semibold text-[#1d1d1f] mb-2">Protected system folders</h4>
              <p class="text-[#424245]">As of v8.18.0, Sortio refuses to organize, watch, or move files out of system or root locations: the filesystem root, OS directories like <code>/System</code> or <code>C:\\Windows\\System32</code>, and your home folder itself. Normal subfolders like <code>~/Downloads</code> work as expected. This guard is enforced everywhere: folder selection, automation runs, Space creation, the background watcher, and move destinations.</p>
            </div>
            <div>
              <h4 class="font-semibold text-[#1d1d1f] mb-2">Dry Run before enabling</h4>
              <p class="text-[#424245]">A Dry Run simulates the automation and lists every file it would touch, with zero changes on disk. It costs nothing; use it every time you create or edit a rule.</p>
            </div>
            <div>
              <h4 class="font-semibold text-[#1d1d1f] mb-2">Undo on every run</h4>
              <p class="text-[#424245]">Background runs come with a one-click Undo on the notification. You can also review what ran from the app and reverse it after the fact.</p>
            </div>
            <div>
              <h4 class="font-semibold text-[#1d1d1f] mb-2">No overwrites</h4>
              <p class="text-[#424245]">If a move or rename would land on a name that already exists, Sortio appends a number instead of replacing the existing file.</p>
            </div>
          </div>
        </div>
      `
    },
    {
      id: "plan-limits",
      title: "Plan Limits",
      content: `
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div class="border border-[#e0e0e5] rounded-xl p-5 bg-white/80 backdrop-blur">
            <h4 class="font-semibold text-[#1d1d1f] mb-2">Free</h4>
            <p class="text-[#424245] text-sm">Build automations and run them manually as much as you like. Rule-based sorting is free and unlimited and doesn't use your AI allowance at all. Watch-folder auto-run is not included.</p>
          </div>
          <div class="border border-[#06c]/20 rounded-xl p-5 bg-[#06c]/5 backdrop-blur">
            <h4 class="font-semibold text-[#1d1d1f] mb-2">Pro / Team</h4>
            <p class="text-[#424245] text-sm">Auto-run on new files (watch folders) with no cap on how many folders you watch. Practical limits come from your OS file-watch resources, not from Sortio.</p>
          </div>
        </div>

        <p class="text-[#1d1d1f] text-lg">
          Your AI allowance is only used for AI operations: generating a rule with AI and running an AI
          sort each use a small part of it. Running automations, refining rules with follow-up prompts,
          and Dry Runs are always free.
        </p>
      `
    },
    {
      id: "tips",
      title: "Tips & Gotchas",
      content: `
        <div class="border border-[#e0e0e5] rounded-xl bg-white/80 backdrop-blur overflow-hidden">
          <div class="p-6 space-y-5">
            <div>
              <h4 class="font-semibold text-[#1d1d1f] mb-2">Start manual, then graduate to auto-run</h4>
              <p class="text-[#424245]">Run a new automation manually a few times and check the results. Once it behaves, switch the Space to "When files are added" and let it work in the background.</p>
            </div>
            <div>
              <h4 class="font-semibold text-[#1d1d1f] mb-2">Avoid overlapping watched folders</h4>
              <p class="text-[#424245]">Two Spaces watching nested folders (one on ~/Documents, another on ~/Documents/Work) can both act on the same file. Keep watched folders disjoint, or be deliberate about which Space owns which files.</p>
            </div>
            <div>
              <h4 class="font-semibold text-[#1d1d1f] mb-2">Use priority and stop-on-match for overlapping rules</h4>
              <p class="text-[#424245]">Rules run in priority order (lower numbers first). Enable stop-on-match so a file claimed by one rule isn't also processed by the next. Details in the <a href="/docs/rules-engine" class="text-[#06c]">Rules Engine</a> docs.</p>
            </div>
            <div>
              <h4 class="font-semibold text-[#1d1d1f] mb-2">The "Indexing..." spinner is not your automation running</h4>
              <p class="text-[#424245]">The lower-left "Indexing N/total" indicator reports Knowledge Graph ingestion (Sortio reading file contents), not automation execution. Nothing moves on disk while it's up. To confirm an automation ran, check the run notification or the Space's Automations tab.</p>
            </div>
            <div>
              <h4 class="font-semibold text-[#1d1d1f] mb-2">Give fallbacks to optional fields</h4>
              <p class="text-[#424245]">Sorting photos by date taken? Use <code>{file.exif.datetime|Other}</code> so screenshots and videos without EXIF data go to a folder instead of failing.</p>
            </div>
          </div>
        </div>
      `
    }
  ]
};

export default watchFoldersAutomationsContent;
